/**
 * Affiliates Page - Affiliate performance metrics tracked via UTM Campaign
 *
 * Affiliates: Storie, Sona, Kay, Dejah, Sav, Soph, Cr8Her
 * Filtered by sourceCampaign (EOC) / utm_campaign (Application/Optin)
 * Acquisition breakdown by UTM source (pie chart)
 */

import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { MetricCard, MetricCardSkeleton, StatCard, StatCardSkeleton } from "../components";
import { useDateRange } from "../context/DateRangeContext";
import { useTheme } from "../context/ThemeContext";
import { ResponsivePie } from "@nivo/pie";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { branding } from "../config/branding";

const DEFAULT_AFFILIATE_NAMES = ["Storie", "Sona", "Kay", "Dejah", "Cr8Her"];
const DEFAULT_COMMISSION_RATE = 15;

// Icons
const DollarIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const TrendingUpIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
  </svg>
);

const ChartIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

const PercentIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
  </svg>
);

const UsersIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const CogIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

// Formatters
const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);

// Payout amounts keep cents so they read exactly as the Payouts page pays them.
const formatPayout = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);

const formatPercent = (value: number) => `${value.toFixed(1)}%`;

// Nivo pie theme
const getNivoPieTheme = () => ({
  labels: { text: { fontSize: 12, fontWeight: 600 } },
  legends: { text: { fontSize: 11 } },
});

// Pie tooltip
const PieChartTooltip = ({ datum }: { datum: { id: string | number; value: number; color: string; data: { percentage: number } } }) => (
  <div className="bg-card text-foreground border border-border rounded-lg shadow-xl px-3 py-2 text-sm">
    <div className="flex items-center gap-2">
      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: datum.color }} />
      <span className="font-medium">{datum.id}</span>
    </div>
    <div className="mt-1 text-muted-foreground">
      {datum.value.toLocaleString()} ({datum.data.percentage.toFixed(1)}%)
    </div>
  </div>
);

const toNivoPieData = (data: Array<{ name: string; value: number }>, colors: string[]) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  return data
    .map((item, index) => ({
      id: item.name,
      label: item.name,
      value: item.value,
      color: colors[index % colors.length],
      percentage: total > 0 ? (item.value / total) * 100 : 0,
    }))
    .sort((a, b) => b.value - a.value);
};

interface AffiliateMetrics {
  totalCashCollected: number;
  totalRevenue: number;
  newCashCollected: number;
  mrrCash: number;
  showRate: number;
  closeRate: number;
  callsDue: number;
  callsTaken: number;
  callsClosed: number;
  avgCashPerCall: number;
  avgCashPerClose: number;
  avgCashPerApplication: number;
  avgCashPerOptin: number;
  totalApplications: number;
  totalOptins: number;
}

interface BreakdownItem {
  _id: string;
  count: number;
}

// Keep alias for compatibility
type SourceBreakdownItem = BreakdownItem;

interface LeadsData {
  totalLeads: number;
  fullApplications: number;
  partialApplications: number;
  totalApplications: number;
  totalOptins: number;
  bookedCalls: number;
}

interface OutcomeItem {
  _id: string;
  count: number;
  cash: number;
}

interface AffiliatePerformanceItem {
  name: string;
  totalCash: number;
  totalRevenue: number;
  callCount: number;
  callsClosed: number;
  avgCashPerCall: number;
  optins: number;
  apps: number;
}

// Commission exactly as the Payouts page pays it (one shared server-side calculation:
// Eastern business days, latest opt-in wins within 15 days of first payment, refunds follow the lead).
interface AffiliatePayoutRow {
  name: string;
  grossRevenue: number;
  revenue: number;
  rate: number;
  commission: number;
}

interface AffiliatePayouts {
  totalCash: number;
  refunds: number;
  payoutCashFactor: number;
  rows: AffiliatePayoutRow[];
}

interface CashTrendPoint {
  date: string;
  value: number;
}

// Affiliate Settings Modal (tabbed: Commission Rates + Manage Affiliates)
function CommissionModal({
  rates,
  affiliateNames,
  onSave,
  onSaveNames,
  onClose,
}: {
  rates: Record<string, number>;
  affiliateNames: string[];
  onSave: (rates: Record<string, number>) => void;
  onSaveNames: (names: string[]) => void;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<"rates" | "manage">("rates");
  const [localRates, setLocalRates] = useState<Record<string, number>>({ ...rates });
  const [localNames, setLocalNames] = useState<string[]>([...affiliateNames]);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSaveRates = async () => {
    setSaving(true);
    try {
      const res = await fetch("/api/affiliates/config", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rates: localRates }),
      });
      const data = await res.json() as { success: boolean };
      if (data.success) { onSave(localRates); onClose(); }
    } catch (err) {
      console.error("Failed to save commission rates:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNames = async () => {
    setSaving(true);
    try {
      // Clean up rates for removed affiliates
      const cleanedRates = Object.fromEntries(
        Object.entries(localRates).filter(([name]) => localNames.includes(name))
      );
      const res = await fetch("/api/affiliates/config", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ affiliateNames: localNames, rates: cleanedRates }),
      });
      const data = await res.json() as { success: boolean };
      if (data.success) { onSaveNames(localNames); onSave(cleanedRates); onClose(); }
    } catch (err) {
      console.error("Failed to save affiliate names:", err);
    } finally {
      setSaving(false);
    }
  };

  const addAffiliate = () => {
    const trimmed = newName.trim();
    if (trimmed && !localNames.includes(trimmed)) {
      setLocalNames((prev) => [...prev, trimmed]);
      setNewName("");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Affiliate Settings</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-5 bg-muted/30 rounded-lg p-1">
          <button
            onClick={() => setTab("rates")}
            className={`flex-1 text-sm py-1.5 rounded-md font-medium transition-all ${
              tab === "rates" ? "bg-card shadow text-foreground" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Commission Rates
          </button>
          <button
            onClick={() => setTab("manage")}
            className={`flex-1 text-sm py-1.5 rounded-md font-medium transition-all ${
              tab === "manage" ? "bg-card shadow text-foreground" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Manage Affiliates
          </button>
        </div>

        {tab === "rates" && (
          <>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {localNames.map((name) => (
                <div key={name} className="flex items-center justify-between gap-4">
                  <span className="font-medium text-sm">{name}</span>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step={0.5}
                      value={localRates[name] ?? DEFAULT_COMMISSION_RATE}
                      onChange={(e) =>
                        setLocalRates((prev) => ({ ...prev, [name]: parseFloat(e.target.value) || 0 }))
                      }
                      className="w-20 text-right px-3 py-1.5 text-sm rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-[var(--brand-ring)]"
                    />
                    <span className="text-sm text-muted-foreground">%</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-input hover:bg-muted transition-colors">
                Cancel
              </button>
              <button
                onClick={handleSaveRates}
                disabled={saving}
                className="px-4 py-2 text-sm rounded-lg bg-gradient-to-r from-[var(--brand-gradient-start)] to-[var(--brand-gradient-end)] text-white hover:opacity-90 transition-opacity disabled:opacity-60"
              >
                {saving ? "Saving..." : "Save Rates"}
              </button>
            </div>
          </>
        )}

        {tab === "manage" && (
          <>
            <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
              {localNames.map((name) => (
                <div key={name} className="flex items-center justify-between bg-muted/20 rounded-lg px-3 py-2">
                  <span className="font-medium text-sm">{name}</span>
                  <button
                    onClick={() => setLocalNames((prev) => prev.filter((n) => n !== name))}
                    className="text-muted-foreground hover:text-red-500 transition-colors ml-2"
                    title={`Remove ${name}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addAffiliate()}
                placeholder="Add affiliate name..."
                className="flex-1 px-3 py-2 text-sm rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-[var(--brand-ring)]"
              />
              <button
                onClick={addAffiliate}
                className="px-3 py-2 text-sm rounded-lg bg-gradient-to-r from-[var(--brand-gradient-start)] to-[var(--brand-gradient-end)] text-white hover:opacity-90 transition-opacity"
              >
                Add
              </button>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-input hover:bg-muted transition-colors">
                Cancel
              </button>
              <button
                onClick={handleSaveNames}
                disabled={saving}
                className="px-4 py-2 text-sm rounded-lg bg-gradient-to-r from-[var(--brand-gradient-start)] to-[var(--brand-gradient-end)] text-white hover:opacity-90 transition-opacity disabled:opacity-60"
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}

export function Affiliates() {
  const { dateRange: globalDateRange, formatForApi } = useDateRange();
  const { theme } = useTheme();
  const isCrimson = theme === "crimson";

  const [isLoading, setIsLoading] = useState(true);
  const [metrics, setMetrics] = useState<AffiliateMetrics | null>(null);
  const [cashTrend, setCashTrend] = useState<CashTrendPoint[]>([]);
  const [sourceBreakdown, setSourceBreakdown] = useState<SourceBreakdownItem[]>([]);
  const [mediumBreakdown, setMediumBreakdown] = useState<BreakdownItem[]>([]);
  const [outcomeBreakdown, setOutcomeBreakdown] = useState<OutcomeItem[]>([]);
  const [affiliatePerformance, setAffiliatePerformance] = useState<AffiliatePerformanceItem[]>([]);
  const [leadsData, setLeadsData] = useState<LeadsData | null>(null);
  const [affiliatePayouts, setAffiliatePayouts] = useState<AffiliatePayouts | null>(null);

  // Affiliate filter (single selection or "all")
  const [selectedAffiliate, setSelectedAffiliate] = useState<string>("all");

  // Affiliate names (dynamic, from DB)
  const [affiliateNames, setAffiliateNames] = useState<string[]>(DEFAULT_AFFILIATE_NAMES);

  // Commission rates
  const [commissionRates, setCommissionRates] = useState<Record<string, number>>({});
  const [showCommissionModal, setShowCommissionModal] = useState(false);

  // Fetch commission config once
  useEffect(() => {
    fetch("/api/affiliates/config")
      .then((r) => r.json())
      .then((data: { success: boolean; rates: Record<string, number>; affiliateNames: string[] }) => {
        if (data.success) {
          setCommissionRates(data.rates);
          if (data.affiliateNames?.length) setAffiliateNames(data.affiliateNames);
        }
      })
      .catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      const { startDate, endDate } = formatForApi();
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      // Affiliate payout liability is cash accounting; use the EOC cash-recorded timestamp.
      params.set("dateField", "timestamp");
      if (selectedAffiliate !== "all") params.set("affiliate", selectedAffiliate);

      const response = await fetch(`/api/affiliates/summary?${params.toString()}`);
      const result = await response.json() as {
        success: boolean;
        data: {
          metrics: AffiliateMetrics;
          cashTrend: CashTrendPoint[];
          sourceBreakdown: SourceBreakdownItem[];
          mediumBreakdown: BreakdownItem[];
          outcomeBreakdown: OutcomeItem[];
          affiliatePerformance: AffiliatePerformanceItem[];
          leadsData: LeadsData;
          affiliatePayouts?: AffiliatePayouts;
        };
      };
      if (result.success) {
        const { data } = result;
        setMetrics(data.metrics);
        setCashTrend(data.cashTrend || []);
        setSourceBreakdown(data.sourceBreakdown || []);
        setMediumBreakdown(data.mediumBreakdown || []);
        setOutcomeBreakdown(data.outcomeBreakdown || []);
        setAffiliatePerformance(data.affiliatePerformance || []);
        setLeadsData(data.leadsData || null);
        setAffiliatePayouts(data.affiliatePayouts || null);
      }
    } catch (error) {
      console.error("Failed to fetch affiliate data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [globalDateRange, formatForApi, selectedAffiliate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Commission calculation
  const getRate = (name: string) => commissionRates[name] ?? DEFAULT_COMMISSION_RATE;

  // Commission comes from the server's shared payout calculation (the same numbers the Payouts
  // page shows), so this tab and the Payouts page can never disagree. The local math below is only
  // a fallback for a server that did not return it.
  const payoutRowFor = (name: string) =>
    affiliatePayouts?.rows.find((row) => row.name.toLowerCase() === name.toLowerCase());
  const commissionFor = (affiliate: AffiliatePerformanceItem) =>
    payoutRowFor(affiliate.name)?.commission ?? affiliate.totalCash * (getRate(affiliate.name) / 100);
  const payoutBaseFor = (affiliate: AffiliatePerformanceItem) =>
    payoutRowFor(affiliate.name)?.revenue ?? affiliate.totalCash;
  const selectedPayoutRows = affiliatePayouts
    ? affiliatePayouts.rows.filter((row) => selectedAffiliate === "all" || row.name.toLowerCase() === selectedAffiliate.toLowerCase())
    : null;

  const affiliateCommission = (() => {
    if (selectedPayoutRows) return selectedPayoutRows.reduce((sum, row) => sum + row.commission, 0);
    if (selectedAffiliate !== "all") {
      return (metrics?.newCashCollected || 0) * (getRate(selectedAffiliate) / 100);
    }
    // Aggregate: weighted by each affiliate's cash * their rate
    let totalCommission = 0;
    for (const perf of affiliatePerformance) {
      const rate = getRate(perf.name) / 100;
      totalCommission += perf.totalCash * rate;
    }
    return totalCommission;
  })();
  const commissionBase = selectedPayoutRows
    ? selectedPayoutRows.reduce((sum, row) => sum + row.revenue, 0)
    : (metrics?.newCashCollected || 0);
  // Refunds follow the lead: only refunds on the selected affiliate's own leads are netted from her cash.
  const refundsNetted = selectedPayoutRows
    ? selectedPayoutRows.reduce((sum, row) => sum + (row.grossRevenue - row.revenue), 0)
    : 0;

  const commissionRate = selectedAffiliate !== "all"
    ? getRate(selectedAffiliate)
    : (commissionBase > 0
        ? (affiliateCommission / commissionBase) * 100
        : DEFAULT_COMMISSION_RATE);

  const chartColors = branding.colors.chart;
  const PIE_COLORS = chartColors;

  const sourcePieData = sourceBreakdown
    .filter((s) => s._id && s._id.trim() !== "")
    .map((s) => ({ name: s._id, value: s.count }));

  const mediumPieData = mediumBreakdown
    .filter((m) => m._id && m._id.trim() !== "")
    .map((m) => ({ name: m._id, value: m.count }));

  const outcomePieData = outcomeBreakdown
    .filter((o) => o._id && o._id.trim() !== "")
    .slice(0, 6)
    .map((o) => ({ name: o._id, value: o.count }));

  if (isLoading && !metrics) {
    return (
      <div className="space-y-6">
        <div className="grid gap-6 grid-cols-2 md:grid-cols-4">
          {[0, 1, 2, 3].map((i) => <MetricCardSkeleton key={i} index={i} />)}
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="h-24 rounded-2xl border bg-muted/30 animate-pulse"
        />
        <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-5">
          {[0, 1, 2, 3, 4].map((i) => <StatCardSkeleton key={i} index={i} />)}
        </div>
        <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
          {[0, 1, 2, 3].map((i) => <StatCardSkeleton key={i} index={i} />)}
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          {[0, 1].map((i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 + i * 0.1 }}
              className="bg-card rounded-xl border border-border p-6"
            >
              <div className="h-5 w-40 rounded bg-muted animate-pulse mb-4" />
              <div className="h-[300px] rounded-lg bg-muted/30 animate-pulse" />
            </motion.div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="flex items-center justify-between flex-wrap gap-3"
      >
        <div className="flex items-center gap-3">
          <motion.h1
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="text-2xl font-bold tracking-tight"
          >
            Affiliates
          </motion.h1>
        </div>
        <button
          onClick={() => setShowCommissionModal(true)}
          className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-input bg-card hover:bg-muted transition-colors"
        >
          <CogIcon />
          Commission Rates
        </button>
      </motion.div>

      {/* Affiliate Filter */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="flex flex-wrap gap-2"
      >
        <button
          onClick={() => setSelectedAffiliate("all")}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
            selectedAffiliate === "all"
              ? "bg-gradient-to-r from-[var(--brand-gradient-start)] to-[var(--brand-gradient-end)] text-white shadow-md"
              : "border border-input bg-card hover:bg-muted"
          }`}
        >
          All Affiliates
        </button>
        {affiliateNames.map((name) => (
          <button
            key={name}
            onClick={() => setSelectedAffiliate(name === selectedAffiliate ? "all" : name)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              selectedAffiliate === name
                ? "bg-gradient-to-r from-[var(--brand-gradient-start)] to-[var(--brand-gradient-end)] text-white shadow-md"
                : "border border-input bg-card hover:bg-muted"
            }`}
          >
            {name}
          </button>
        ))}
      </motion.div>

      {/* Top Metric Cards */}
      <div className="grid gap-6 grid-cols-2 md:grid-cols-4">
        <MetricCard
          label="Cash Collected"
          value={formatCurrency(metrics?.totalCashCollected || 0)}
          icon={<DollarIcon />}
          color="brand"
          data={cashTrend.map((t) => t.value)}
          tooltipFormatter={formatCurrency}
          index={0}
        />
        <MetricCard
          label="Revenue Generated"
          value={formatCurrency(metrics?.totalRevenue || 0)}
          icon={<TrendingUpIcon />}
          color="green"
          data={cashTrend.map((t) => t.value)}
          tooltipFormatter={formatCurrency}
          index={1}
        />
        <MetricCard
          label="New Cash (excl. MRR)"
          value={formatCurrency(metrics?.newCashCollected || 0)}
          icon={<ChartIcon />}
          color="blue"
          data={[]}
          index={2}
        />
        <MetricCard
          label="MRR"
          value={formatCurrency(metrics?.mrrCash || 0)}
          icon={<DollarIcon />}
          color="brand"
          data={[]}
          index={3}
        />
      </div>

      {/* Affiliate Commission Card */}
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
        whileHover={{ y: -4, transition: { duration: 0.2 } }}
        className="relative overflow-hidden border rounded-2xl p-6 transition-shadow duration-500 bg-card"
        style={isCrimson ? {
          background: "white",
          borderColor: "rgba(0,0,0,0.08)",
          boxShadow: "0 2px 16px -4px rgba(0,0,0,0.1)",
        } : {
          background: "linear-gradient(to bottom right, rgba(99,102,241,0.45), rgba(99,102,241,0.3), rgba(99,102,241,0.12))",
          borderColor: "rgba(99,102,241,0.5)",
          boxShadow: "0 0 40px -5px rgba(99,102,241,0.55)",
        }}
      >
        <div className="relative flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <motion.div
                className="size-5 text-indigo-400"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <PercentIcon />
              </motion.div>
              <span className="text-xs md:text-sm font-semibold text-foreground/80 uppercase tracking-wider">
                Affiliate Commission ({formatPercent(commissionRate)})
              </span>
            </div>
            <p className="text-2xl md:text-4xl font-bold text-foreground tracking-tight">
              {affiliatePayouts ? formatPayout(affiliateCommission) : formatCurrency(affiliateCommission)}
            </p>
            <p className="text-xs text-muted-foreground mt-1.5 md:mt-2">
              {selectedAffiliate === "all"
                ? `Weighted average across all affiliates`
                : `${formatPercent(commissionRate)} of new cash collected for ${selectedAffiliate}`}
              {affiliatePayouts
                ? ` · on ${formatPayout(commissionBase)}${refundsNetted > 0 ? ` after ${formatPayout(refundsNetted)} refunds on her leads netted` : ""} — same as the Payouts page`
                : ""}
            </p>
          </div>
          <button
            onClick={() => setShowCommissionModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-white/20 bg-white/10 hover:bg-white/20 transition-colors text-foreground/80"
          >
            <CogIcon />
            Adjust
          </button>
        </div>
      </motion.div>

      {/* Performance Stats */}
      <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-5">
        <StatCard label="Show Rate" value={formatPercent(metrics?.showRate || 0)} index={0} />
        <StatCard label="Close Rate" value={formatPercent(metrics?.closeRate || 0)} index={1} />
        <StatCard label="Calls Due" value={(metrics?.callsDue || 0).toLocaleString()} index={2} />
        <StatCard label="Calls Taken" value={(metrics?.callsTaken || 0).toLocaleString()} index={3} />
        <StatCard label="Calls Closed" value={(metrics?.callsClosed || 0).toLocaleString()} index={4} />
      </div>

      {/* Average Stats */}
      <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
        <StatCard label="Avg. Cash / Call" value={formatCurrency(metrics?.avgCashPerCall || 0)} index={5} />
        <StatCard label="Avg. Cash / Close" value={formatCurrency(metrics?.avgCashPerClose || 0)} index={6} />
        <StatCard label="Avg. Cash / Application" value={formatCurrency(metrics?.avgCashPerApplication || 0)} index={7} />
        <StatCard label="Avg. Cash / Opt-in" value={formatCurrency(metrics?.avgCashPerOptin || 0)} index={8} />
      </div>

      {/* Charts Row */}
      <div className="grid gap-6 md:grid-cols-3">
        {/* Acquisition Breakdown (by UTM Source) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="bg-card rounded-xl border border-border p-6 overflow-hidden"
        >
          <h3 className="text-lg font-semibold mb-1">Acquisition Breakdown</h3>
          <p className="text-sm text-muted-foreground mb-4">Leads by UTM Source</p>
          <div className="h-[320px]">
            {sourcePieData.length > 0 ? (
              <ResponsivePie
                data={toNivoPieData(sourcePieData, PIE_COLORS)}
                margin={{ top: 20, right: 80, bottom: 20, left: 80 }}
                innerRadius={0.5}
                padAngle={1}
                cornerRadius={3}
                activeOuterRadiusOffset={8}
                sortByValue={false}
                colors={{ datum: "data.color" }}
                borderWidth={2}
                borderColor={{ from: "color", modifiers: [["darker", 0.6]] }}
                enableArcLinkLabels={true}
                arcLinkLabelsSkipAngle={10}
                arcLinkLabelsTextColor={{ from: "color", modifiers: [["brighter", 1]] }}
                arcLinkLabelsThickness={2}
                arcLinkLabelsColor={{ from: "color" }}
                arcLinkLabel={(d) => String(d.label)}
                enableArcLabels={true}
                arcLabelsSkipAngle={10}
                arcLabel={(d) => `${d.data.percentage.toFixed(1)}%`}
                arcLabelsTextColor="#ffffff"
                theme={getNivoPieTheme()}
                tooltip={PieChartTooltip}
              />
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                No acquisition data available
              </div>
            )}
          </div>
        </motion.div>

        {/* Funnel Breakdown (by UTM Medium) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.65, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="bg-card rounded-xl border border-border p-6 overflow-hidden"
        >
          <h3 className="text-lg font-semibold mb-1">Funnel Breakdown</h3>
          <p className="text-sm text-muted-foreground mb-4">Leads by Source Funnel (UTM Medium)</p>
          <div className="h-[320px]">
            {mediumPieData.length > 0 ? (
              <ResponsivePie
                data={toNivoPieData(mediumPieData, PIE_COLORS)}
                margin={{ top: 20, right: 80, bottom: 20, left: 80 }}
                innerRadius={0.5}
                padAngle={1}
                cornerRadius={3}
                activeOuterRadiusOffset={8}
                sortByValue={false}
                colors={{ datum: "data.color" }}
                borderWidth={2}
                borderColor={{ from: "color", modifiers: [["darker", 0.6]] }}
                enableArcLinkLabels={true}
                arcLinkLabelsSkipAngle={10}
                arcLinkLabelsTextColor={{ from: "color", modifiers: [["brighter", 1]] }}
                arcLinkLabelsThickness={2}
                arcLinkLabelsColor={{ from: "color" }}
                arcLinkLabel={(d) => String(d.label)}
                enableArcLabels={true}
                arcLabelsSkipAngle={10}
                arcLabel={(d) => `${d.data.percentage.toFixed(1)}%`}
                arcLabelsTextColor="#ffffff"
                theme={getNivoPieTheme()}
                tooltip={PieChartTooltip}
              />
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                No funnel data available
              </div>
            )}
          </div>
        </motion.div>

        {/* Deal Status Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="bg-card rounded-xl border border-border p-6 overflow-hidden"
        >
          <h3 className="text-lg font-semibold mb-1">Deal Status Breakdown</h3>
          <p className="text-sm text-muted-foreground mb-4">Call outcomes distribution</p>
          <div className="h-[320px]">
            {outcomePieData.length > 0 ? (
              <ResponsivePie
                data={toNivoPieData(outcomePieData, PIE_COLORS)}
                margin={{ top: 20, right: 80, bottom: 20, left: 80 }}
                innerRadius={0.5}
                padAngle={1}
                cornerRadius={3}
                activeOuterRadiusOffset={8}
                sortByValue={false}
                colors={{ datum: "data.color" }}
                borderWidth={2}
                borderColor={{ from: "color", modifiers: [["darker", 0.6]] }}
                enableArcLinkLabels={true}
                arcLinkLabelsSkipAngle={10}
                arcLinkLabelsTextColor={{ from: "color", modifiers: [["brighter", 1]] }}
                arcLinkLabelsThickness={2}
                arcLinkLabelsColor={{ from: "color" }}
                arcLinkLabel={(d) => String(d.label)}
                enableArcLabels={true}
                arcLabelsSkipAngle={10}
                arcLabel={(d) => `${d.data.percentage.toFixed(1)}%`}
                arcLabelsTextColor="#ffffff"
                theme={getNivoPieTheme()}
                tooltip={PieChartTooltip}
              />
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                No deal status data available
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Affiliate Bar Charts */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.72, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="grid gap-6 md:grid-cols-3"
      >
        {/* Opt-ins by affiliate */}
        <div className="bg-card rounded-xl border border-border p-6">
          <h3 className="text-lg font-semibold mb-1">Opt-ins by Affiliate</h3>
          <p className="text-sm text-muted-foreground mb-4">Total opt-ins attributed via UTM campaign</p>
          <div className="h-[240px]">
            {affiliatePerformance.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={affiliatePerformance.map((a) => ({ name: a.name, value: a.optins }))} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "rgba(255,255,255,0.5)" }} />
                  <YAxis tick={{ fontSize: 11, fill: "rgba(255,255,255,0.5)" }} />
                  <Tooltip
                    contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8 }}
                    formatter={(v: number) => [v.toLocaleString(), "Opt-ins"]}
                  />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {affiliatePerformance.map((_, i) => (
                      <Cell key={i} fill={branding.colors.chart[i % branding.colors.chart.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground text-sm">No data</div>
            )}
          </div>
        </div>

        {/* Cash by affiliate */}
        <div className="bg-card rounded-xl border border-border p-6">
          <h3 className="text-lg font-semibold mb-1">Cash by Affiliate</h3>
          <p className="text-sm text-muted-foreground mb-4">Cash collected from affiliate-referred closes</p>
          <div className="h-[240px]">
            {affiliatePerformance.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={affiliatePerformance.map((a) => ({ name: a.name, value: a.totalCash }))} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "rgba(255,255,255,0.5)" }} />
                  <YAxis tick={{ fontSize: 11, fill: "rgba(255,255,255,0.5)" }} tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8 }}
                    formatter={(v: number) => [formatCurrency(v), "Cash"]}
                  />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {affiliatePerformance.map((_, i) => (
                      <Cell key={i} fill={branding.colors.chart[i % branding.colors.chart.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground text-sm">No data</div>
            )}
          </div>
        </div>

        {/* Applications by affiliate */}
        <div className="bg-card rounded-xl border border-border p-6">
          <h3 className="text-lg font-semibold mb-1">Applications by Affiliate</h3>
          <p className="text-sm text-muted-foreground mb-4">Applications attributed via UTM campaign</p>
          <div className="h-[240px]">
            {affiliatePerformance.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={affiliatePerformance.map((a) => ({ name: a.name, value: a.apps }))} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "rgba(255,255,255,0.5)" }} />
                  <YAxis tick={{ fontSize: 11, fill: "rgba(255,255,255,0.5)" }} />
                  <Tooltip
                    contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8 }}
                    formatter={(v: number) => [v.toLocaleString(), "Applications"]}
                  />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {affiliatePerformance.map((_, i) => (
                      <Cell key={i} fill={branding.colors.chart[i % branding.colors.chart.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground text-sm">No data</div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Leads Summary */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.75, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="bg-card rounded-xl border border-border p-6"
      >
        <div className="flex items-center gap-2 mb-5">
          <UsersIcon />
          <div>
            <h3 className="text-lg font-semibold">Leads Overview</h3>
            <p className="text-sm text-muted-foreground">Applications, opt-ins &amp; booked calls in this period</p>
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* Total Leads */}
          <div className="rounded-xl border border-border bg-muted/20 p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Total Leads</p>
            <p className="text-3xl font-bold">{(leadsData?.totalLeads ?? 0).toLocaleString()}</p>
            <p className="text-xs text-muted-foreground mt-1">Apps + Opt-ins combined</p>
          </div>
          {/* Full Applications */}
          <div className="rounded-xl border border-border bg-muted/20 p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Full Applications</p>
            <p className="text-3xl font-bold text-[var(--brand-primary)]">{(leadsData?.fullApplications ?? 0).toLocaleString()}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {leadsData && leadsData.totalApplications > 0
                ? `${((leadsData.fullApplications / leadsData.totalApplications) * 100).toFixed(1)}% of applications`
                : "Complete form submissions"}
            </p>
          </div>
          {/* Partial Opt-ins */}
          <div className="rounded-xl border border-border bg-muted/20 p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Partial Opt-ins</p>
            <p className="text-3xl font-bold text-[var(--brand-secondary)]">{(leadsData?.partialApplications ?? 0).toLocaleString()}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {leadsData && leadsData.totalApplications > 0
                ? `${((leadsData.partialApplications / leadsData.totalApplications) * 100).toFixed(1)}% of applications`
                : "Partial form submissions"}
            </p>
          </div>
          {/* Booked Calls */}
          <div className="rounded-xl border border-border bg-muted/20 p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Booked Calls</p>
            <p className="text-3xl font-bold text-green-500">{(leadsData?.bookedCalls ?? 0).toLocaleString()}</p>
            <p className="text-xs text-muted-foreground mt-1">From call bookings (not EOC)</p>
          </div>
        </div>
      </motion.div>

      {/* Affiliate Performance Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="bg-card rounded-xl border border-border overflow-hidden"
      >
        <div className="p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <UsersIcon />
            <div>
              <h3 className="text-lg font-semibold">Affiliate Performance</h3>
              <p className="text-sm text-muted-foreground">Per-affiliate breakdown with commission</p>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left p-4 font-medium text-muted-foreground">Affiliate</th>
                <th className="text-right p-4 font-medium text-muted-foreground">Opt-ins</th>
                <th className="text-right p-4 font-medium text-muted-foreground">Apps</th>
                <th className="text-right p-4 font-medium text-muted-foreground">Cash Collected</th>
                <th className="text-right p-4 font-medium text-muted-foreground">Calls</th>
                <th className="text-right p-4 font-medium text-muted-foreground">Closed</th>
                <th className="text-right p-4 font-medium text-muted-foreground">Avg / Call</th>
                <th className="text-right p-4 font-medium text-muted-foreground">Rate</th>
                <th className="text-right p-4 font-medium text-muted-foreground">Commission</th>
              </tr>
            </thead>
            <tbody>
              {affiliatePerformance.length === 0 ? (
                <tr>
                  <td colSpan={9} className="p-8 text-center text-muted-foreground">
                    No affiliate data available for this period
                  </td>
                </tr>
              ) : (
                affiliatePerformance.map((affiliate, i) => {
                  const rate = payoutRowFor(affiliate.name)?.rate ?? getRate(affiliate.name);
                  const commission = commissionFor(affiliate);
                  const payoutBase = payoutBaseFor(affiliate);
                  return (
                    <tr
                      key={affiliate.name}
                      className={`border-b border-border transition-colors hover:bg-muted/30 ${i % 2 === 0 ? "" : "bg-muted/20"}`}
                    >
                      <td className="p-4 font-medium">{affiliate.name}</td>
                      <td className="p-4 text-right text-muted-foreground">{affiliate.optins.toLocaleString()}</td>
                      <td className="p-4 text-right text-muted-foreground">{affiliate.apps.toLocaleString()}</td>
                      <td className="p-4 text-right font-medium text-green-600 dark:text-green-400">
                        {formatCurrency(affiliate.totalCash)}
                      </td>
                      <td className="p-4 text-right text-muted-foreground">{affiliate.callCount}</td>
                      <td className="p-4 text-right text-muted-foreground">{affiliate.callsClosed}</td>
                      <td className="p-4 text-right text-muted-foreground">
                        {formatCurrency(affiliate.avgCashPerCall)}
                      </td>
                      <td className="p-4 text-right">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]">
                          {formatPercent(rate)}
                        </span>
                      </td>
                      <td className="p-4 text-right font-semibold text-[var(--brand-primary)]">
                        {affiliatePayouts ? formatPayout(commission) : formatCurrency(commission)}
                        {Math.abs(payoutBase - affiliate.totalCash) >= 0.005 && (
                          <div className="text-xs font-normal text-muted-foreground">on {formatPayout(payoutBase)} after refunds</div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            {affiliatePerformance.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-border bg-muted/30">
                  <td className="p-4 font-semibold">Total</td>
                  <td className="p-4 text-right font-semibold">
                    {affiliatePerformance.reduce((s, a) => s + a.optins, 0).toLocaleString()}
                  </td>
                  <td className="p-4 text-right font-semibold">
                    {affiliatePerformance.reduce((s, a) => s + a.apps, 0).toLocaleString()}
                  </td>
                  <td className="p-4 text-right font-semibold">
                    {formatCurrency(affiliatePerformance.reduce((s, a) => s + a.totalCash, 0))}
                  </td>
                  <td className="p-4 text-right font-semibold">
                    {affiliatePerformance.reduce((s, a) => s + a.callCount, 0)}
                  </td>
                  <td className="p-4 text-right font-semibold">
                    {affiliatePerformance.reduce((s, a) => s + a.callsClosed, 0)}
                  </td>
                  <td colSpan={2} className="p-4" />
                  <td className="p-4 text-right font-semibold text-[var(--brand-primary)]">
                    {(affiliatePayouts ? formatPayout : formatCurrency)(affiliatePerformance.reduce((s, a) => s + commissionFor(a), 0))}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </motion.div>

      {/* Commission Modal */}
      {showCommissionModal && (
        <CommissionModal
          rates={commissionRates}
          affiliateNames={affiliateNames}
          onSave={setCommissionRates}
          onSaveNames={(names) => {
            setAffiliateNames(names);
            // If selected affiliate was removed, reset to "all"
            if (selectedAffiliate !== "all" && !names.includes(selectedAffiliate)) {
              setSelectedAffiliate("all");
            }
          }}
          onClose={() => setShowCommissionModal(false)}
        />
      )}
    </div>
  );
}
